// Composable de ciclo de vida do SDK WAVOIP (outbound-only).
// Possui um singleton em escopo de módulo do `Wavoip` para sobreviver à
// desmontagem dos componentes Vue (Pitfall 4 — a conexão Socket.IO do SDK
// seria vazada se o singleton fosse de escopo de componente). Expõe
// `startCall(phone, contactId)` + `endCall()` e fia os 6 eventos do SDK
// (`peerAccept`/`peerReject`/`unanswered`/`ended` no callOutgoing +
// `error`/`ended` no callActive) nas mutações do `useWavoipCallsStore`.
// Origem: ADR 0007.
//
// Limitação conhecida (v0.1): Pinia é por aba — uma segunda aba do mesmo
// usuário iniciando `startCall` falhará no lado do servidor WAVOIP
// (token = dispositivo = navegador). Aceito; documentado em
// `.planning/phases/03-call-button-sdk-pinia-store/03-CONTEXT.md` <deferred>.

import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Wavoip } from '@wavoip/wavoip-api';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useWavoipCallsStore } from '../stores/wavoipCalls';

// Singletons em escopo de módulo (D-15) — sobrevivem à desmontagem dos
// componentes; o usuário pode navegar entre rotas sem perder a conexão SDK.
// `previousToken` é capturado em `startCall` ANTES do `currentUser` mudar,
// porque o watcher de logout precisa do token antigo para chamar
// `removeDevices([previousToken])` (o `currentUser` já apontou para o novo).
let wavoipInstance = null;
let previousToken = null;
let cleanupWatcher = null;
let callOutgoingRef = null;
let callActiveRef = null;

// Libera a captura de microfone do SDK. O `mediaManager` é um singleton na
// instância `Wavoip` e é adquirido já no `startCall` (a offer WebRTC precisa
// da mídia local antes do callee atender). O SDK só PARA a captura quando o
// RTCPeerConnection fecha (`transport.stop()`), o que acontece apenas no
// `end()` LOCAL de uma chamada ATIVA. Em encerramentos remotos
// (peerReject/unanswered/ended) e ao cancelar durante o dialing o pc não
// fecha, então o track do mic seguiria capturando e o navegador manteria o
// indicador de microfone aceso (UAT SC4). Por isso chamamos `stopMedia()`
// explicitamente em todo teardown. Optional chaining = no-op seguro se a
// instância ainda não existe ou o shape interno do SDK mudar.
const releaseMedia = () => {
  wavoipInstance?.mediaManager?.stopMedia?.();
};

const TIMEOUT_MS = 15000;
// `Promise.race` deixa o timer pendente quando o `promise` ganha — sob
// dials sequenciais ou re-imports HMR isso acumula timers e segura GC
// das closures que capturam `reject`. `finally(clearTimeout)` garante
// limpeza determinística independente de qual lado da race vencer.
const withTimeout = (promise, ms) => {
  let timerId;
  const timer = new Promise((_, reject) => {
    timerId = setTimeout(() => reject(new Error('TIMEOUT')), ms);
  });
  return Promise.race([promise, timer]).finally(() => clearTimeout(timerId));
};

// Watcher idempotente em escopo de módulo. O composable pode ser invocado
// a partir de múltiplos componentes; o `watch` precisa rodar exatamente uma
// vez por carga do módulo. Dispara em troca de user-id E em logout (id → undefined).
// D-20: NÃO dispara quando o token muda no mesmo user-id.
function ensureUserWatcher(currentUser, store) {
  if (cleanupWatcher) return;
  cleanupWatcher = watch(
    () => currentUser.value?.id,
    (newId, oldId) => {
      if (oldId && newId !== oldId && wavoipInstance) {
        wavoipInstance.removeDevices([previousToken]);
        wavoipInstance = null;
        previousToken = null;
        callOutgoingRef = null;
        callActiveRef = null;
        store.reset();
      }
    }
  );
}

/**
 * Composable: lifecycle do SDK WAVOIP.
 *
 * D-20: NÃO dispara cleanup quando o token rotaciona para o MESMO user-id
 * (apenas em troca de user-id / logout). Rotação de token "ao vivo" foi
 * adiada (v0.2 WAVOIP-TOKEN-LIVE-RELOAD); até lá, o usuário precisa
 * recarregar a página para que o novo token seja consumido.
 *
 * @returns {{ startCall: Function, endCall: Function }}
 */
export function useWavoipCallSession() {
  const { t } = useI18n();
  const store = useWavoipCallsStore();
  const currentUser = useMapGetter('getCurrentUser');

  ensureUserWatcher(currentUser, store);

  // Handlers definidos aninhados para fechar sobre `t` e `store`.
  // `attachActiveListeners` é declarada antes de `attachOutgoingListeners`
  // porque o handler de `peerAccept` faz o ref-swap callOutgoing → callActive
  // e chama `attachActiveListeners(call)` imediatamente.
  // Em `error`/`ended` da chamada ativa (incl. hangup remoto) liberamos o
  // microfone via `releaseMedia()` antes de resetar o store — ver nota no
  // helper sobre o leak de mídia (UAT SC4).
  function attachActiveListeners(call) {
    call.on('error', () => {
      useAlert(t('CDC.WAVOIP.toast.error_generic'));
      releaseMedia();
      store.reset();
    });
    call.on('ended', () => {
      releaseMedia();
      store.reset();
    });
  }

  function attachOutgoingListeners(call) {
    // O objeto outgoing do SDK expõe APENAS `on` — não tem `off` (somente o
    // EventEmitter interno tem). Chamar `call.off(...)` lança
    // "off is not a function" e abortaria o handler do peerAccept ANTES do
    // ref-swap, deixando `callActiveRef` nulo → Encerrar vira no-op (UAT SC3).
    // Por isso desligamos o `ended` do outgoing via flag `swapped` em vez de
    // `off`: após o pickup, o objeto da chamada ATIVA passa a ser a fonte do
    // `ended`, e o handler outgoing vira no-op (evita double `store.reset`).
    let swapped = false;
    const outgoingEnded = () => {
      if (!swapped) {
        releaseMedia();
        store.reset();
      }
    };
    call.on('peerAccept', activeCall => {
      swapped = true;
      store.setInCall();
      // O SDK entrega o objeto da CHAMADA ATIVA no payload do peerAccept
      // (`i.emit("peerAccept", activeCall)`). Esse objeto tem o `end()` que
      // emite `call.end` (encerra uma chamada JÁ ATENDIDA). O objeto outgoing
      // original só tem `end()` → `call.cancel`, que o servidor ignora numa
      // chamada ativa. Por isso a ref PRECISA apontar para `activeCall` —
      // caso contrário o botão Encerrar vira no-op durante o in_call (UAT SC3).
      callActiveRef = activeCall;
      attachActiveListeners(activeCall);
    });
    call.on('peerReject', () => {
      useAlert(t('CDC.WAVOIP.toast.reject'));
      releaseMedia();
      store.reset();
    });
    call.on('unanswered', () => {
      useAlert(t('CDC.WAVOIP.toast.unanswered'));
      releaseMedia();
      store.reset();
    });
    call.on('ended', outgoingEnded);
  }

  const startCall = async (phone, contactId) => {
    // Defense-in-depth: o botão já está desabilitado quando não está idle.
    if (store.status !== 'idle') return;

    store.setDialing(phone, contactId);

    try {
      // Lazy init — singleton sobrevive entre chamadas; WebSocket só abre
      // no primeiro startCall do agente (Pitfall 4 + D-15). DENTRO do try:
      // o construtor do SDK pode lançar síncronamente (ex.: AudioWorklet
      // indisponível fora de secure context) e precisa cair no catch para
      // resetar o store e exibir o toast — senão o widget fica preso em
      // "dialing" para sempre (UAT SC1 / SC5).
      if (!wavoipInstance) {
        previousToken = currentUser.value?.custom_wavoip_token;
        wavoipInstance = new Wavoip({ tokens: [previousToken] });
      }

      const { call, err } = await withTimeout(
        wavoipInstance.startCall({ to: phone }),
        TIMEOUT_MS
      );
      if (err) throw new Error(err.message || 'WAVOIP_ERROR');
      callOutgoingRef = call;
      attachOutgoingListeners(call);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[WAVOIP startCall]', e?.message);
      // Chave i18n inline em cada ramo (em vez de string dinâmica) — evita
      // o aviso `@intlify/vue-i18n/no-dynamic-keys` e mantém os IDs
      // rastreáveis por grep no catálogo CDC.
      if (e?.message === 'TIMEOUT') {
        useAlert(t('CDC.WAVOIP.toast.signaling_timeout'));
      } else {
        useAlert(t('CDC.WAVOIP.toast.error_generic'));
      }
      // O SDK adquire o microfone na criação da offer (dentro de startCall);
      // num timeout/erro precisamos liberá-lo, senão fica preso (UAT SC4/SC5).
      releaseMedia();
      store.reset();
    }
  };

  // SDK-05 literal: exposto, sem UI em Phase 3. Phase 4 widget (botão
  // "Encerrar") consome este endCall.
  const endCall = () => {
    // Cancelar durante o dialing emite `call.cancel` mas NÃO fecha o pc, então
    // o mic não é liberado pelo SDK; em in_call o `end()` ativo já chama
    // `transport.stop()` → `stopMedia()`. `releaseMedia()` cobre ambos (no-op
    // quando não há stream) e garante o microfone desligado de imediato.
    if (store.status === 'dialing' && callOutgoingRef) callOutgoingRef.end();
    else if (store.status === 'in_call' && callActiveRef) callActiveRef.end();
    releaseMedia();
  };

  return { startCall, endCall };
}
